'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { generateRoomId } from '@/lib/client-utils';
import { useZuper } from '@/lib/zuper/ZuperContext';
import { LoginPage } from '@/lib/zuper/LoginPage';
import { ZuperUser } from '@/lib/zuper/types';
import styles from '../styles/Zuper.module.css';
import toast from 'react-hot-toast';
import { upsertUsers, subscribeToUsers, updateUserOnlineStatus } from '@/lib/firebase/users';
import { createRoom, getRoomsByUser, subscribeToIncomingCalls, declineCall, subscribeToRoom, cancelOutgoingCall } from '@/lib/firebase/rooms';
import { FirestoreRoom, FirestoreUser, IncomingCall } from '@/lib/firebase/types';
import { IncomingCallModal } from '@/lib/IncomingCallModal';
import { OutgoingCallModal } from '@/lib/OutgoingCallModal';
import {
  IconSearch,
  IconLayoutGrid,
  IconList,
  IconVideo,
  IconDeviceVisionPro,
  IconPhoneCall,
  IconChevronLeft,
  IconChevronRight,
  IconLogout,
  IconSun,
  IconMoon,
} from '@tabler/icons-react';
import { useTheme } from '@/lib/ThemeContext';

// Hero slides with multilingual content
const HERO_SLIDES = [
  {
    image: '/hero-telco-v2.png',
    headlines: [
      { lang: 'en', title: "Telco Deserves the World's #1 Field Service Management Platform", desc: 'Empower your field technicians with AI-powered remote assist, smart glass integration, and real-time collaboration — built for the demands of telecom.' },
      { lang: 'es', title: 'Telco Merece la Plataforma #1 del Mundo en Gestión de Servicios de Campo', desc: 'Capacite a sus técnicos de campo con asistencia remota impulsada por IA, integración con gafas inteligentes y colaboración en tiempo real — diseñada para las exigencias de las telecomunicaciones.' },
      { lang: 'zh', title: '电信行业值得拥有全球第一的现场服务管理平台', desc: '通过AI驱动的远程协助、智能眼镜集成和实时协作，为您的现场技术人员赋能——专为电信行业的需求而打造。' },
      { lang: 'fr', title: 'Les Télécoms Méritent la Plateforme #1 Mondiale de Gestion des Services Terrain', desc: "Donnez à vos techniciens de terrain les moyens d'agir grâce à l'assistance à distance par IA, l'intégration de lunettes connectées et la collaboration en temps réel — conçue pour les exigences des télécoms." },
      { lang: 'ko', title: '통신 산업은 세계 최고의 현장 서비스 관리 플랫폼을 누릴 자격이 있습니다', desc: 'AI 기반 원격 지원, 스마트 글래스 통합, 실시간 협업으로 현장 기술자에게 힘을 실어주세요 — 통신 산업의 요구에 맞춰 설계되었습니다.' },
    ],
  },
  {
    image: '/hero-roofing.jpg',
    objectPosition: '60% top',
    headlines: [
      { lang: 'en', title: "The World's #1 Field Service Platform for Roofing", desc: 'Streamline inspections, estimates, and repairs with smart scheduling, real-time tracking, and remote assist — purpose-built for roofing professionals.' },
      { lang: 'es', title: 'La Plataforma #1 del Mundo en Servicios de Campo para Techos', desc: 'Optimice inspecciones, presupuestos y reparaciones con programación inteligente, seguimiento en tiempo real y asistencia remota — diseñada para profesionales de techos.' },
      { lang: 'zh', title: '全球第一的屋顶行业现场服务管理平台', desc: '通过智能调度、实时跟踪和远程协助，简化检查、估算和维修流程——专为屋顶行业专业人士打造。' },
      { lang: 'fr', title: 'La Plateforme #1 Mondiale de Services Terrain pour la Toiture', desc: "Rationalisez les inspections, devis et réparations grâce à la planification intelligente, au suivi en temps réel et à l'assistance à distance — conçue pour les professionnels de la toiture." },
      { lang: 'ko', title: '세계 최고의 지붕 업계 현장 서비스 플랫폼', desc: '스마트 스케줄링, 실시간 추적, 원격 지원으로 검사, 견적, 수리를 간소화하세요 — 지붕 전문가를 위해 설계되었습니다.' },
    ],
  },
];

const SLIDE_INTERVAL = 10000; // 10s per slide
const LANG_INTERVAL = 5000;  // 5s per language
const FADE_DURATION = 500;

function HeroSlideshow() {
  const [slideIndex, setSlideIndex] = useState(0);
  const [langIndex, setLangIndex] = useState(0);
  const [textFading, setTextFading] = useState(false);
  const [slideFading, setSlideFading] = useState(false);

  // Cycle languages within a slide
  useEffect(() => {
    const interval = setInterval(() => {
      setTextFading(true);
      setTimeout(() => {
        setLangIndex((prev) => (prev + 1) % HERO_SLIDES[0].headlines.length);
        setTextFading(false);
      }, FADE_DURATION);
    }, LANG_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Cycle slides
  useEffect(() => {
    const interval = setInterval(() => {
      setSlideFading(true);
      setTimeout(() => {
        setSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length);
        setLangIndex(0);
        setSlideFading(false);
      }, FADE_DURATION);
    }, SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const slide = HERO_SLIDES[slideIndex];
  const headline = slide.headlines[langIndex % slide.headlines.length];

  return (
    <>
      {HERO_SLIDES.map((s, i) => (
        <img
          key={s.image}
          src={s.image}
          alt=""
          className={`${styles.heroImage} ${i === slideIndex && !slideFading ? styles.heroImageActive : styles.heroImageInactive}`}
          style={s.objectPosition ? { objectPosition: s.objectPosition } : undefined}
        />
      ))}
      <div className={styles.heroOverlay} />
      <div className={styles.heroContent}>
        <div className={styles.heroText}>
          <h1
            className={`${styles.heroTitle} ${textFading || slideFading ? styles.heroTitleFadeOut : styles.heroTitleFadeIn}`}
          >
            {headline.title}
          </h1>
          <p
            className={`${styles.heroSubtitle} ${textFading || slideFading ? styles.heroTitleFadeOut : styles.heroTitleFadeIn}`}
          >
            {headline.desc}
          </p>
        </div>
        <div className={styles.heroQr}>
          <img
            src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fzuper.co%3Futm_source%3Dmwc-2026"
            alt="Scan to visit zuper.co"
            className={styles.heroQrImage}
          />
        </div>
        <div className={styles.heroIndicators}>
          {HERO_SLIDES.map((_, i) => (
            <span
              key={i}
              className={`${styles.heroIndicator} ${i === slideIndex ? styles.heroIndicatorActive : ''}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

// Pagination constants
const ITEMS_PER_PAGE = 12;
const SEARCH_DEBOUNCE_MS = 400;
const MIN_LOADING_MS = 600; // Minimum time to show shimmer for smooth UX

// Custom hook for debouncing
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Skeleton components for shimmer loading
function UserCardSkeleton() {
  return (
    <div className={`${styles.userCard} ${styles.skeleton}`}>
      <div className={styles.cardHeader}>
        <div className={`${styles.skeletonAvatar} ${styles.shimmer}`} />
        <div className={styles.badgeContainer}>
          <div className={`${styles.skeletonBadge} ${styles.shimmer}`} />
        </div>
      </div>
      <div className={styles.cardFooter}>
        <div className={styles.userInfo}>
          <div className={`${styles.skeletonText} ${styles.skeletonTextLarge} ${styles.shimmer}`} />
          <div className={`${styles.skeletonText} ${styles.skeletonTextSmall} ${styles.shimmer}`} />
        </div>
      </div>
    </div>
  );
}

function UserListRowSkeleton() {
  return (
    <div className={styles.listRow}>
      <div className={styles.listUserInfo}>
        <div className={`${styles.skeletonAvatarSmall} ${styles.shimmer}`} />
        <div className={`${styles.skeletonText} ${styles.skeletonTextMedium} ${styles.shimmer}`} />
      </div>
      <div className={styles.listCell}>
        <div className={`${styles.skeletonText} ${styles.skeletonTextSmall} ${styles.shimmer}`} />
      </div>
      <div className={styles.listCell}>
        <div className={`${styles.skeletonBadge} ${styles.shimmer}`} />
      </div>
      <div className={styles.listCell}>
        <div className={`${styles.skeletonBadge} ${styles.shimmer}`} />
      </div>
      <div className={styles.listActions}>
        <div className={`${styles.skeletonButton} ${styles.shimmer}`} />
      </div>
    </div>
  );
}

function CallHistoryRowSkeleton() {
  return (
    <div className={styles.listRow}>
      <div className={styles.listUserInfo}>
        <div className={`${styles.skeletonAvatarSmall} ${styles.shimmer}`} />
        <div className={`${styles.skeletonText} ${styles.skeletonTextMedium} ${styles.shimmer}`} />
      </div>
      <div className={styles.listCell}>
        <div className={`${styles.skeletonText} ${styles.skeletonTextSmall} ${styles.shimmer}`} />
      </div>
      <div className={styles.listCell}>
        <div className={`${styles.skeletonText} ${styles.skeletonTextSmall} ${styles.shimmer}`} />
      </div>
      <div className={styles.listCell}>
        <div className={`${styles.skeletonBadge} ${styles.shimmer}`} />
      </div>
      <div className={styles.listActions} />
    </div>
  );
}

// Avatar colors based on name
const AVATAR_COLORS = ['avatarPurple', 'avatarGreen', 'avatarBlue', 'avatarOrange', 'avatarPink'];

function getAvatarColor(name: string): string {
  const index = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

// Extended user type with Firestore status fields
type UserWithStatus = ZuperUser & {
  current_room_id: string | null;
  is_glass_connected: boolean;
};

// User List Row Component (for table view)
function UserListRow({
  user,
  onStartSession,
}: {
  user: UserWithStatus;
  onStartSession: (user: UserWithStatus) => void;
}) {
  const fullName = `${user.first_name} ${user.last_name}`;
  const initials = getInitials(user.first_name, user.last_name);
  const avatarColorClass = getAvatarColor(fullName);
  const isOnline = user.is_active;
  const isBusy = !!user.current_room_id;

  // Determine status: Busy > Online > Offline
  const getStatusBadge = () => {
    if (isBusy) {
      return (
        <span className={`${styles.badge} ${styles.badgeBusy}`}>
          <IconPhoneCall size={12} /> On a Call
        </span>
      );
    }
    if (isOnline) {
      return (
        <span className={`${styles.badge} ${styles.badgeOnline}`}>
          <span className={styles.onlineDot} /> Online
        </span>
      );
    }
    return (
      <span className={`${styles.badge} ${styles.badgeOffline}`}>
        Offline
      </span>
    );
  };

  return (
    <div className={`${styles.listRow} ${!isOnline && !isBusy ? styles.listRowOffline : ''}`}>
      <div className={styles.listUserInfo}>
        {user.profile_picture ? (
          <img src={user.profile_picture} alt={fullName} className={styles.listAvatar} />
        ) : (
          <div className={`${styles.listAvatarFallback} ${styles[avatarColorClass]}`}>
            {initials}
          </div>
        )}
        <span className={styles.listUserName}>{fullName}</span>
      </div>
      <div className={styles.listCell}>
        {user.designation || user.role?.role_name || 'Team Member'}
      </div>
      <div className={styles.listCell}>
        {user.is_glass_connected && (
          <span className={`${styles.badge} ${styles.badgeGlass}`}>
            <IconDeviceVisionPro size={12} /> Glass
          </span>
        )}
      </div>
      <div className={styles.listCell}>
        {getStatusBadge()}
      </div>
      <div className={styles.listActions}>
        {isOnline && !isBusy && (
          <button className={styles.startButton} onClick={() => onStartSession(user)}>
            <IconVideo size={16} />
            Start Session
          </button>
        )}
      </div>
    </div>
  );
}

// Format duration from seconds to human readable
function formatDuration(seconds: number | null): string {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

// Format timestamp to relative time
function formatRelativeTime(timestamp: { toDate: () => Date } | null): string {
  if (!timestamp) return '-';
  const date = timestamp.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Call History Row Component
function CallHistoryRow({
  room,
  currentUserUid,
}: {
  room: FirestoreRoom;
  currentUserUid: string;
}) {
  // Get other participants (excluding current user)
  const otherParticipants = room.participants.filter((p) => p.user_uid !== currentUserUid);
  const participantNames = otherParticipants.map((p) => p.name).join(', ') || 'Unknown';

  // Get first participant for avatar
  const firstParticipant = otherParticipants[0];
  const initials = firstParticipant ? firstParticipant.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase() : '?';
  const avatarColorClass = getAvatarColor(participantNames);

  const isActive = room.status === 'active';
  // Check if call was declined by any participant
  const wasDeclined = room.participants.some((p) => p.status === 'declined');
  // Check if call was actually connected (any participant joined)
  const wasConnected = room.participants.some((p) => p.joined_at !== null);

  // Determine display status
  const getStatusInfo = () => {
    if (isActive) {
      return { label: 'Active', badgeClass: styles.badgeOnline, showDot: true };
    }
    if (wasDeclined && !wasConnected) {
      return { label: 'Declined', badgeClass: styles.badgeDeclined, showDot: false };
    }
    return { label: 'Ended', badgeClass: styles.badgeOffline, showDot: false };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={styles.listRow}>
      <div className={styles.listUserInfo}>
        {firstParticipant?.profile_picture ? (
          <img src={firstParticipant.profile_picture} alt={participantNames} className={styles.listAvatar} />
        ) : (
          <div className={`${styles.listAvatarFallback} ${styles[avatarColorClass]}`}>
            {initials}
          </div>
        )}
        <span className={styles.listUserName}>{participantNames}</span>
      </div>
      <div className={styles.listCell}>
        {formatDuration(room.duration_seconds)}
      </div>
      <div className={styles.listCell}>
        {formatRelativeTime(room.created_at as { toDate: () => Date } | null)}
      </div>
      <div className={styles.listCell}>
        <span className={`${styles.badge} ${statusInfo.badgeClass}`}>
          {statusInfo.showDot && <span className={styles.onlineDot} />}
          {statusInfo.label}
        </span>
      </div>
      <div className={styles.listActions}>
        {/* No join button - users should use the incoming call notification */}
      </div>
    </div>
  );
}

// User Card Component
function UserCard({
  user,
  onStartSession,
}: {
  user: UserWithStatus;
  onStartSession: (user: UserWithStatus) => void;
}) {
  const fullName = `${user.first_name} ${user.last_name}`;
  const initials = getInitials(user.first_name, user.last_name);
  const avatarColorClass = getAvatarColor(fullName);
  const isOnline = user.is_active;
  const isBusy = !!user.current_room_id;

  // Determine status badge
  const getStatusBadge = () => {
    if (isBusy) {
      return (
        <span className={`${styles.badge} ${styles.badgeBusy}`}>
          <IconPhoneCall size={12} /> On a Call
        </span>
      );
    }
    if (isOnline) {
      return (
        <span className={`${styles.badge} ${styles.badgeOnline}`}>
          <span className={styles.onlineDot} /> Online
        </span>
      );
    }
    return (
      <span className={`${styles.badge} ${styles.badgeOffline}`}>
        Offline
      </span>
    );
  };

  return (
    <div className={`${styles.userCard} ${!isOnline && !isBusy ? styles.userCardOffline : ''}`}>
      {/* Header: Avatar + Badges */}
      <div className={styles.cardHeader}>
        {user.profile_picture ? (
          <img
            src={user.profile_picture}
            alt={fullName}
            className={styles.userAvatar}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className={`${styles.userAvatarFallback} ${styles[avatarColorClass]}`}>
            {initials}
          </div>
        )}

        <div className={styles.badgeContainer}>
          {user.is_glass_connected && (
            <span className={`${styles.badge} ${styles.badgeGlass}`}>
              <IconDeviceVisionPro size={12} /> Glass
            </span>
          )}
          {getStatusBadge()}
        </div>
      </div>

      {/* Footer: Name, Designation, Button */}
      <div className={styles.cardFooter}>
        <div className={styles.userInfo}>
          <h3 className={styles.userName}>{fullName}</h3>
          <p className={styles.userDesignation}>
            {user.designation || user.role?.role_name || 'Team Member'}
          </p>
        </div>

        {isOnline && !isBusy && (
          <button className={styles.startButton} onClick={() => onStartSession(user)}>
            <IconVideo size={16} />
            Start Session
          </button>
        )}
      </div>
    </div>
  );
}

export default function Page() {
  const router = useRouter();
  const { settings, currentUser, isLoading: contextLoading, isAuthenticated, logout } = useZuper();
  const { theme, toggleTheme } = useTheme();
  const [teamUsers, setTeamUsers] = useState<ZuperUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'technicians' | 'history'>('technicians');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [callHistory, setCallHistory] = useState<FirestoreRoom[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [firestoreUsers, setFirestoreUsers] = useState<Map<string, FirestoreUser>>(new Map());
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [outgoingCall, setOutgoingCall] = useState<{
    roomId: string;
    targetName: string;
    targetProfilePicture: string | null;
    targetUid: string;
    status: 'calling' | 'connecting' | 'declined' | 'no_answer';
  } | null>(null);
  const outgoingCallUnsubscribeRef = React.useRef<(() => void) | null>(null);

  // Pagination state
  const [usersPage, setUsersPage] = useState(1);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);

  // Debounce search query for API calls
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);

  // Fetch team users when settings are available
  const fetchTeamUsers = useCallback(async (page: number = 1, keyword: string = '') => {
    if (!settings) return;

    setIsLoading(true);
    setError(null);

    try {
      let url = `/api/zuper/users?team_uid=${settings.team_uid}&limit=${ITEMS_PER_PAGE}&page=${page}`;
      if (keyword.trim()) {
        url += `&keyword=${encodeURIComponent(keyword.trim())}`;
      }

      // Run fetch and minimum delay in parallel for smooth shimmer UX
      const [response] = await Promise.all([
        fetch(url, {
          headers: {
            'Authorization': `Bearer ${settings.auth_token}`,
          },
        }),
        new Promise(resolve => setTimeout(resolve, MIN_LOADING_MS)),
      ]);

      if (!response.ok) {
        throw new Error(`Failed to fetch team users: ${response.status}`);
      }

      const data = await response.json();
      if (data.type === 'success' && Array.isArray(data.data)) {
        setTeamUsers(data.data);
        // Get total count from API response (total_records field)
        if (data.total_records !== undefined) {
          setTotalUsersCount(data.total_records);
        }

        // Sync users to Firestore (fire and forget)
        upsertUsers(data.data).catch((err) => {
          console.error('Failed to sync users to Firestore:', err);
        });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch team users';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [settings]);

  // Fetch users when page or debounced search query changes
  useEffect(() => {
    if (settings) {
      fetchTeamUsers(usersPage, debouncedSearchQuery);
    }
  }, [usersPage, debouncedSearchQuery, settings, fetchTeamUsers]);

  // Subscribe to real-time Firestore user status updates
  useEffect(() => {
    const unsubscribe = subscribeToUsers(
      (users) => {
        const userMap = new Map<string, FirestoreUser>();
        users.forEach((user) => {
          userMap.set(user.user_uid, user);
        });
        setFirestoreUsers(userMap);
      },
      (error) => {
        console.error('Firestore subscription error:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  // Set current user online status when app loads
  useEffect(() => {
    if (!settings?.user_uid) return;

    // Set online when app loads
    updateUserOnlineStatus(settings.user_uid, true).catch((err) => {
      console.error('Failed to set user online:', err);
    });

    // Set offline when app closes (beforeunload)
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on page unload
      // Note: This won't work for Firestore directly, so we use a sync approach
      updateUserOnlineStatus(settings.user_uid, false).catch(() => {
        // Ignore errors on unload
      });
    };

    // Set offline when tab becomes hidden (optional - may be too aggressive)
    // const handleVisibilityChange = () => {
    //   if (document.hidden) {
    //     updateUserOnlineStatus(settings.user_uid, false);
    //   } else {
    //     updateUserOnlineStatus(settings.user_uid, true);
    //   }
    // };

    window.addEventListener('beforeunload', handleBeforeUnload);
    // document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Set offline when component unmounts (navigation away)
      updateUserOnlineStatus(settings.user_uid, false).catch(() => {});
    };
  }, [settings?.user_uid]);

  // Subscribe to incoming calls
  useEffect(() => {
    if (!settings?.user_uid) return;

    console.log('[IncomingCalls] Subscribing for user:', settings.user_uid);

    const unsubscribe = subscribeToIncomingCalls(
      settings.user_uid,
      (call) => {
        console.log('[IncomingCalls] Received:', call);
        setIncomingCall(call);
      },
      (error) => {
        console.error('[IncomingCalls] Subscription error:', error);
      }
    );

    return () => {
      console.log('[IncomingCalls] Unsubscribing');
      unsubscribe();
    };
  }, [settings?.user_uid]);

  // Handle accepting an incoming call
  const handleAcceptCall = useCallback(() => {
    if (!incomingCall || !currentUser) return;

    const username = `${currentUser.first_name} ${currentUser.last_name}`;
    router.push(`/rooms/${incomingCall.room_id}?skipPreJoin=true&username=${encodeURIComponent(username)}`);
    setIncomingCall(null);
  }, [incomingCall, currentUser, router]);

  // Handle declining an incoming call
  const handleDeclineCall = useCallback(async () => {
    if (!incomingCall || !settings?.user_uid) return;

    try {
      await declineCall(incomingCall.room_id, settings.user_uid);
    } catch (err) {
      console.error('Failed to decline call:', err);
    }
    setIncomingCall(null);
  }, [incomingCall, settings?.user_uid]);

  // Handle canceling an outgoing call
  const handleCancelOutgoingCall = useCallback(async () => {
    if (!outgoingCall) return;

    // Unsubscribe from room updates
    if (outgoingCallUnsubscribeRef.current) {
      outgoingCallUnsubscribeRef.current();
      outgoingCallUnsubscribeRef.current = null;
    }

    try {
      await cancelOutgoingCall(outgoingCall.roomId);
    } catch (err) {
      console.error('Failed to cancel call:', err);
    }
    setOutgoingCall(null);
  }, [outgoingCall]);

  // Handle when outgoing call is connected (target accepted)
  const handleOutgoingCallConnected = useCallback(() => {
    if (!outgoingCall || !currentUser) return;

    // Unsubscribe from room updates
    if (outgoingCallUnsubscribeRef.current) {
      outgoingCallUnsubscribeRef.current();
      outgoingCallUnsubscribeRef.current = null;
    }

    const username = `${currentUser.first_name} ${currentUser.last_name}`;
    router.push(`/rooms/${outgoingCall.roomId}?skipPreJoin=true&username=${encodeURIComponent(username)}`);
    setOutgoingCall(null);
  }, [outgoingCall, currentUser, router]);

  // Handle when outgoing call is declined
  const handleOutgoingCallDeclined = useCallback(() => {
    // Unsubscribe from room updates
    if (outgoingCallUnsubscribeRef.current) {
      outgoingCallUnsubscribeRef.current();
      outgoingCallUnsubscribeRef.current = null;
    }
    setOutgoingCall(null);
    toast.error('Call was declined');
  }, []);

  const handleStartSession = useCallback(
    async (targetUser: ZuperUser) => {
      if (!currentUser || !settings) {
        toast.error('Current user not loaded. Please check your settings.');
        return;
      }

      const roomId = generateRoomId();
      const username = `${currentUser.first_name} ${currentUser.last_name}`;
      const targetName = `${targetUser.first_name} ${targetUser.last_name}`;

      // Create room in Firestore
      try {
        await createRoom({
          room_id: roomId,
          created_by: currentUser.user_uid,
          created_by_name: username,
          created_by_profile_picture: currentUser.profile_picture,
          team_uid: settings.team_uid,
          target_user: {
            user_uid: targetUser.user_uid,
            name: targetName,
            profile_picture: targetUser.profile_picture,
          },
        });

        // Show outgoing call modal
        setOutgoingCall({
          roomId,
          targetName,
          targetProfilePicture: targetUser.profile_picture,
          targetUid: targetUser.user_uid,
          status: 'calling',
        });

        // Subscribe to room updates to detect when target user responds
        const unsubscribe = subscribeToRoom(roomId, (room) => {
          console.log('[subscribeToRoom] Room update received:', room?.room_id, 'status:', room?.status);
          if (!room) return;

          // Find target user's participant record
          const targetParticipant = room.participants.find(
            (p) => p.user_uid === targetUser.user_uid
          );

          console.log('[subscribeToRoom] Target participant:', targetParticipant?.user_uid, 'status:', targetParticipant?.status);
          console.log('[subscribeToRoom] All participants:', room.participants.map(p => ({ uid: p.user_uid, status: p.status })));

          if (targetParticipant) {
            if (targetParticipant.status === 'on_call' || targetParticipant.status === 'connecting') {
              // Target user accepted - update status to connecting
              console.log('[subscribeToRoom] Target accepted! Setting outgoingCall status to connecting');
              setOutgoingCall((prev) => prev ? { ...prev, status: 'connecting' } : null);
            } else if (targetParticipant.status === 'declined') {
              // Target user declined
              console.log('[subscribeToRoom] Target declined!');
              setOutgoingCall((prev) => prev ? { ...prev, status: 'declined' } : null);
              return; // Don't check room.status - declined takes precedence
            }
          }

          // Check if room was ended (but not due to decline - that's handled above)
          if (room.status === 'ended') {
            setOutgoingCall((prev) => {
              // Only set no_answer if we're still in 'calling' state
              if (prev && prev.status === 'calling') {
                return { ...prev, status: 'no_answer' };
              }
              return prev;
            });
          }
        });

        outgoingCallUnsubscribeRef.current = unsubscribe;

        // Set timeout for no answer (30 seconds)
        setTimeout(() => {
          setOutgoingCall((prev) => {
            if (prev && prev.status === 'calling') {
              return { ...prev, status: 'no_answer' };
            }
            return prev;
          });
        }, 30000);
      } catch (err) {
        console.error('Failed to create room in Firestore:', err);
        toast.error('Failed to start session');
      }
    },
    [currentUser, settings]
  );

  // Fetch call history when history tab is active
  const fetchCallHistory = useCallback(async () => {
    if (!settings?.user_uid) return;

    setHistoryLoading(true);
    try {
      // Run fetch and minimum delay in parallel for smooth shimmer UX
      const [rooms] = await Promise.all([
        getRoomsByUser(settings.user_uid),
        new Promise(resolve => setTimeout(resolve, MIN_LOADING_MS)),
      ]);
      setCallHistory(rooms);
    } catch (err) {
      console.error('Failed to fetch call history:', err);
      toast.error('Failed to load call history');
    } finally {
      setHistoryLoading(false);
    }
  }, [settings?.user_uid]);

  useEffect(() => {
    if (activeTab === 'history' && settings?.user_uid) {
      fetchCallHistory();
    }
  }, [activeTab, settings?.user_uid, fetchCallHistory]);

  // Merge Zuper user data with Firestore status data
  const usersWithFirestoreStatus = useMemo(() => {
    return teamUsers.map((user) => {
      const firestoreUser = firestoreUsers.get(user.user_uid);
      if (firestoreUser) {
        // Override status fields with Firestore data
        return {
          ...user,
          is_active: firestoreUser.is_online,
          is_glass_connected: firestoreUser.is_glass_connected,
          current_room_id: firestoreUser.current_room_id || null,
        };
      }
      // If no Firestore data yet, default to offline/no glass/not on call
      return {
        ...user,
        is_active: false,
        is_glass_connected: false,
        current_room_id: null,
      };
    });
  }, [teamUsers, firestoreUsers]);

  // Filter users by search query and exclude current user
  const filteredUsers = useMemo(() => {
    // First, exclude the current user from the list
    const otherUsers = usersWithFirestoreStatus.filter(
      (user) => user.user_uid !== settings?.user_uid
    );

    // Filter to only show users with emp_code starting with "GLASS"
    const glassUsers = otherUsers.filter(
      (user) => user.emp_code?.startsWith('GLASS')
    );

    // Sort: Online/Busy users first, then offline. Within each group, sort by name.
    // Note: Search filtering is now done server-side via API
    return glassUsers.sort((a, b) => {
      const aIsOnline = a.is_active || !!a.current_room_id;
      const bIsOnline = b.is_active || !!b.current_room_id;

      // Online users come first
      if (aIsOnline && !bIsOnline) return -1;
      if (!aIsOnline && bIsOnline) return 1;

      // Within same online status, sort by name alphabetically
      const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
      const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [usersWithFirestoreStatus, settings?.user_uid]);

  // Reset page when debounced search query changes
  useEffect(() => {
    setUsersPage(1);
  }, [debouncedSearchQuery]);

  // Calculate total pages from server-side count
  const totalUsersPages = Math.ceil(totalUsersCount / ITEMS_PER_PAGE);

  // Paginated call history
  const paginatedHistory = useMemo(() => {
    const startIndex = (historyPage - 1) * ITEMS_PER_PAGE;
    return callHistory.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [callHistory, historyPage]);

  const totalHistoryPages = Math.ceil(callHistory.length / ITEMS_PER_PAGE);

  // Handle logout with confirmation
  const handleLogout = useCallback(() => {
    if (confirm('Are you sure you want to log out?')) {
      logout();
    }
  }, [logout]);

  // Not authenticated - show login page
  if (!isAuthenticated && !contextLoading) {
    return <LoginPage />;
  }

  return (
    <div className={styles.container}>
      {/* Left Hero Panel */}
      <div className={styles.heroPanel}>
        <HeroSlideshow />
      </div>

      {/* Right Content Panel */}
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <h1 className={styles.title}>Remote Assist</h1>
            <div className={styles.headerActions}>
              <div className={styles.headerLogos}>
                <div className={styles.collabLogoWrapper}>
                  <img src="/zuper-logo.png" alt="Zuper" />
                </div>
                <div className={styles.collabLogoWrapper}>
                  <img src="/ericsson-logo.png" alt="Ericsson" />
                </div>
                <div className={styles.collabLogoWrapper}>
                  <img src="/vonage-logo.png" alt="Vonage" />
                </div>
              </div>
              <button
                className={styles.themeToggle}
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{ position: 'static' }}
              >
                {theme === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
              </button>
              <button
                className={styles.logoutButton}
                onClick={handleLogout}
                title="Logout"
              >
                <IconLogout size={20} />
              </button>
            </div>
          </div>
          <p className={styles.description}>
            Start a video call with field technicians. Technicians equipped with Glass can share their
            real-time field of view for enhanced remote support.
          </p>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'technicians' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('technicians')}
          >
            Technicians
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'history' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Call History
          </button>
        </div>

        {activeTab === 'technicians' && (
          <>
            {/* Search and Controls */}
            <div className={styles.controls}>
              <div className={styles.searchContainer}>
                <span className={styles.searchIcon}>
                  <IconSearch size={18} />
                </span>
                <input
                  type="text"
                  placeholder="Search technicians..."
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className={styles.viewToggle}>
                <button
                  className={`${styles.viewButton} ${viewMode === 'grid' ? styles.viewButtonActive : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  <IconLayoutGrid size={18} />
                </button>
                <button
                  className={`${styles.viewButton} ${viewMode === 'list' ? styles.viewButtonActive : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  <IconList size={18} />
                </button>
              </div>
            </div>

            {/* Loading State - Skeleton Loading */}
            {/* Show shimmer while loading OR while settings.user_uid isn't ready (needed for filtering) */}
            {(contextLoading || isLoading || !settings?.user_uid) && viewMode === 'grid' && (
              <div className={styles.usersGrid}>
                {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                  <UserCardSkeleton key={`skeleton-card-${index}`} />
                ))}
              </div>
            )}

            {(contextLoading || isLoading || !settings?.user_uid) && viewMode === 'list' && (
              <div className={styles.usersListContainer}>
                <div className={styles.listHeader}>
                  <div className={styles.listHeaderCell}>Name</div>
                  <div className={styles.listHeaderCell}>Role</div>
                  <div className={styles.listHeaderCell}>Device</div>
                  <div className={styles.listHeaderCell}>Status</div>
                  <div className={styles.listHeaderCell}></div>
                </div>
                <div className={styles.usersList}>
                  {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                    <UserListRowSkeleton key={`skeleton-list-${index}`} />
                  ))}
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className={styles.emptyState}>
                <h3 className={styles.emptyStateTitle}>Error Loading Team</h3>
                <p className={styles.emptyStateText}>{error}</p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                  <button className={styles.startButton} onClick={() => fetchTeamUsers(usersPage)}>
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* Users Grid View */}
            {!isLoading && !contextLoading && settings?.user_uid && !error && filteredUsers.length > 0 && viewMode === 'grid' && (
              <>
                <div className={`${styles.usersGrid} ${styles.fadeIn}`}>
                  {filteredUsers.map((user) => (
                    <UserCard
                      key={user.user_uid}
                      user={user}
                      onStartSession={handleStartSession}
                    />
                  ))}
                </div>
                {totalUsersPages > 1 && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                      disabled={usersPage === 1}
                    >
                      <IconChevronLeft size={16} /> Previous
                    </button>
                    <span className={styles.paginationInfo}>
                      Page {usersPage} of {totalUsersPages} ({totalUsersCount} users)
                    </span>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setUsersPage((p) => Math.min(totalUsersPages, p + 1))}
                      disabled={usersPage === totalUsersPages}
                    >
                      Next <IconChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Users List View (Table) */}
            {!isLoading && !contextLoading && settings?.user_uid && !error && filteredUsers.length > 0 && viewMode === 'list' && (
              <>
                <div className={`${styles.usersListContainer} ${styles.fadeIn}`}>
                  <div className={styles.listHeader}>
                    <div className={styles.listHeaderCell}>Name</div>
                    <div className={styles.listHeaderCell}>Role</div>
                    <div className={styles.listHeaderCell}>Device</div>
                    <div className={styles.listHeaderCell}>Status</div>
                    <div className={styles.listHeaderCell}></div>
                  </div>
                  <div className={`${styles.usersList} ${styles.fadeIn}`}>
                    {filteredUsers.map((user) => (
                      <UserListRow
                        key={user.user_uid}
                        user={user}
                        onStartSession={handleStartSession}
                      />
                    ))}
                  </div>
                </div>
                {totalUsersPages > 1 && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                      disabled={usersPage === 1}
                    >
                      <IconChevronLeft size={16} /> Previous
                    </button>
                    <span className={styles.paginationInfo}>
                      Page {usersPage} of {totalUsersPages} ({totalUsersCount} users)
                    </span>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setUsersPage((p) => Math.min(totalUsersPages, p + 1))}
                      disabled={usersPage === totalUsersPages}
                    >
                      Next <IconChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Empty State */}
            {!isLoading && !contextLoading && settings?.user_uid && !error && filteredUsers.length === 0 && teamUsers.length > 0 && (
              <div className={`${styles.emptyState} ${styles.fadeIn}`}>
                <p className={styles.emptyStateText}>No technicians match your search.</p>
              </div>
            )}

            {!isLoading && !contextLoading && settings?.user_uid && !error && teamUsers.length === 0 && (
              <div className={`${styles.emptyState} ${styles.fadeIn}`}>
                <p className={styles.emptyStateText}>No team members found.</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'history' && (
          <>
            {/* Loading State - Skeleton Loading */}
            {historyLoading && (
              <div className={styles.usersListContainer}>
                <div className={styles.listHeader}>
                  <div className={styles.listHeaderCell}>Participants</div>
                  <div className={styles.listHeaderCell}>Duration</div>
                  <div className={styles.listHeaderCell}>Date</div>
                  <div className={styles.listHeaderCell}>Status</div>
                  <div className={styles.listHeaderCell}></div>
                </div>
                <div className={styles.usersList}>
                  {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                    <CallHistoryRowSkeleton key={`skeleton-history-${index}`} />
                  ))}
                </div>
              </div>
            )}

            {/* Call History List */}
            {!historyLoading && callHistory.length > 0 && (
              <>
                <div className={`${styles.usersListContainer} ${styles.fadeIn}`}>
                  <div className={styles.listHeader}>
                    <div className={styles.listHeaderCell}>Participants</div>
                    <div className={styles.listHeaderCell}>Duration</div>
                    <div className={styles.listHeaderCell}>Date</div>
                    <div className={styles.listHeaderCell}>Status</div>
                    <div className={styles.listHeaderCell}></div>
                  </div>
                  <div className={`${styles.usersList} ${styles.fadeIn}`}>
                    {paginatedHistory.map((room) => (
                      <CallHistoryRow key={room.room_id} room={room} currentUserUid={settings?.user_uid || ''} />
                    ))}
                  </div>
                </div>
                {totalHistoryPages > 1 && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      disabled={historyPage === 1}
                    >
                      <IconChevronLeft size={16} /> Previous
                    </button>
                    <span className={styles.paginationInfo}>
                      Page {historyPage} of {totalHistoryPages} ({callHistory.length} calls)
                    </span>
                    <button
                      className={styles.paginationButton}
                      onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                      disabled={historyPage === totalHistoryPages}
                    >
                      Next <IconChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Empty State */}
            {!historyLoading && callHistory.length === 0 && (
              <div className={`${styles.emptyState} ${styles.fadeIn}`}>
                <h3 className={styles.emptyStateTitle}>No Call History</h3>
                <p className={styles.emptyStateText}>
                  Your recent video calls will appear here. Start a session to begin.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Incoming Call Modal */}
      {incomingCall && (
        <IncomingCallModal
          call={incomingCall}
          onAccept={handleAcceptCall}
          onDecline={handleDeclineCall}
        />
      )}

      {/* Outgoing Call Modal */}
      {outgoingCall && (
        <OutgoingCallModal
          targetName={outgoingCall.targetName}
          targetProfilePicture={outgoingCall.targetProfilePicture}
          status={outgoingCall.status}
          onCancel={handleCancelOutgoingCall}
          onConnected={handleOutgoingCallConnected}
          onDeclined={handleOutgoingCallDeclined}
        />
      )}
    </div>
  );
}
